import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  EVENT_LEFT_PADDING,
  EVENT_RIGHT_PADDING,
  GOAL_SQUARE_SIZE,
  EVENT_COLORS,
  EVENT_HEIGHT,
  Y_PADDING_TOP,
  Y_PADDING_BOTTOM,
  EVENT_TOOLTIP_PADDING,
} from "./constants";

const RoomStatisticsChart = ({ data }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const { pointCount, xGetter, yGetter, label, unit, decimals } = data;
  const mousePosition = useRef({ x: 0, y: 0 });

  const { minY, maxY } = useMemo(() => {
    // Determine the y value range dynamically
    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < pointCount; i++) {
      const y = yGetter(i);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    // Pad y value range if its too small
    const rangeY = maxY - minY;
    if (1.0 > rangeY) {
      const centerY = (maxY + minY) / 2.0;
      minY = centerY - 0.5;
      maxY = centerY + 0.5;
    }

    return { minY, maxY };
  }, [pointCount, yGetter]);

  const draw = useCallback(
    (ctx) => {
      if (!canvasRef.current || !containerRef.current) return;

      canvasRef.current.width = containerRef.current.clientWidth;
      canvasRef.current.height =
        EVENT_HEIGHT + Y_PADDING_TOP + Y_PADDING_BOTTOM;
      ctx.font = '14px "Arial"';

      // Set background color
      ctx.fillStyle = EVENT_COLORS.BG2;
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);

      // Draw X-Axis Line
      ctx.strokeStyle = EVENT_COLORS.Accent3;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(EVENT_LEFT_PADDING, EVENT_HEIGHT + Y_PADDING_TOP);
      ctx.lineTo(
        canvasRef.current.width - EVENT_RIGHT_PADDING,
        EVENT_HEIGHT + Y_PADDING_TOP
      );
      ctx.stroke();

      // Draw Y-Axis Line
      ctx.beginPath();
      ctx.moveTo(EVENT_LEFT_PADDING, Y_PADDING_TOP);
      ctx.lineTo(EVENT_LEFT_PADDING, EVENT_HEIGHT + Y_PADDING_TOP);
      ctx.stroke();

      // Early out if we have no points
      if (pointCount === 0) return;

      // Compute the x step spacing
      const stepX =
        (canvasRef.current.width - EVENT_LEFT_PADDING - EVENT_RIGHT_PADDING) /
        (pointCount - 1);

      // Draw x-axis labels and ticks
      ctx.font = "16px Maitree";
      ctx.fillStyle = EVENT_COLORS.Accent3;
      ctx.textAlign = "center";
      ctx.strokeStyle = EVENT_COLORS.Accent3;
      // for (let i = 0; i < pointCount; i++) {
      //   const x = EVENT_LEFT_PADDING + i * stepX;
        
      //   // X-Axis Ticks
      //   ctx.beginPath();
      //   ctx.moveTo(x, EVENT_HEIGHT + Y_PADDING_TOP);
      //   ctx.lineTo(x, EVENT_HEIGHT + Y_PADDING_TOP + 10); // Draw a small tick
      //   ctx.stroke();

      //   // ctx.fillText(date, x, EVENT_HEIGHT + Y_PADDING_TOP + 25);
      // }

      // TODO(Jon @ Fyelabs): Determine the number of yaxis labels based on height
      const Y_AXIS_LABEL_COUNT = 8;
      let yStep = Math.ceil((maxY - minY) / Y_AXIS_LABEL_COUNT);
      
      // Draw y-axis labels
      ctx.font = "16px Maitree";
      ctx.fillStyle = EVENT_COLORS.Accent3;
      ctx.textAlign = "right";
      for (let yValue = minY; yValue <= maxY; yValue += yStep) {
        const frac = (yValue - minY) / (maxY - minY);
        const y = Y_PADDING_TOP + (EVENT_HEIGHT - frac * EVENT_HEIGHT);
        
        // Y-Axis Ticks
        ctx.beginPath();
        ctx.moveTo(EVENT_LEFT_PADDING - 10, y); // A small tick
        ctx.lineTo(EVENT_LEFT_PADDING, y);
        ctx.stroke();
        
        // Y-Axis Values
        const label = yValue.toFixed(0); 
        ctx.fillText(label, EVENT_LEFT_PADDING - 15, y + 5);
      }

      // Draw vertical label for "Sleep Duration (Hrs)"
      ctx.save();
      ctx.translate(20, EVENT_HEIGHT / 2 + Y_PADDING_TOP);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = "center";
      ctx.fillText(label, 0, 0);
      ctx.restore();

      // Plot lines
      ctx.strokeStyle = EVENT_COLORS.Accent3;
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < pointCount; i++) {
        const x = EVENT_LEFT_PADDING + i * stepX; // Calculate x for each point
        const y =
          Y_PADDING_TOP + (EVENT_HEIGHT - ((yGetter(i) - minY) / (maxY - minY)) * EVENT_HEIGHT);

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();

      // Plot data points
      let hoveredVal = undefined;
      for (let i = 0; i < pointCount; i++) {
        const x = EVENT_LEFT_PADDING + i * stepX; // Calculate x for each point
        const y =
          Y_PADDING_TOP + (EVENT_HEIGHT - ((yGetter(i) - minY) / (maxY - minY)) * EVENT_HEIGHT);

        // Check if mouse is near
        const hovered =
          Math.max(
            Math.abs(mousePosition.current.x - x),
            Math.abs(mousePosition.current.y - y)
          ) <=
          0.5 * GOAL_SQUARE_SIZE;
        if (hovered) {
          hoveredVal = i;
        }
      }

      // Draw tooltip
      if (hoveredVal !== undefined) {
        ctx.font = "16px Maitree";
        ctx.textAlign = "left";

        const tooltipText = `${xGetter(hoveredVal)}: ${yGetter(hoveredVal).toFixed(decimals)}${unit}`;
        const textMetrics = ctx.measureText(tooltipText);
        const tooltipWidth = textMetrics.width + 2 * EVENT_TOOLTIP_PADDING;
        const tooltipHeight =
          textMetrics.actualBoundingBoxAscent +
          textMetrics.actualBoundingBoxDescent +
          2 * EVENT_TOOLTIP_PADDING;

        ctx.fillStyle = EVENT_COLORS.BG2;
        ctx.fillRect(
          mousePosition.current.x - tooltipWidth,
          mousePosition.current.y - tooltipHeight,
          tooltipWidth,
          tooltipHeight
        );

        ctx.strokeStyle = EVENT_COLORS.Accent3;
        ctx.strokeRect(
          mousePosition.current.x - tooltipWidth,
          mousePosition.current.y - tooltipHeight,
          tooltipWidth,
          tooltipHeight
        );

        ctx.fillStyle = EVENT_COLORS.Accent3;
        ctx.fillText(
          tooltipText,
          mousePosition.current.x + EVENT_TOOLTIP_PADDING - tooltipWidth,
          mousePosition.current.y -
            tooltipHeight +
            textMetrics.actualBoundingBoxAscent +
            EVENT_TOOLTIP_PADDING
        );
      }
    },
    [xGetter, yGetter, label, minY, maxY, unit, decimals]
  );

  useEffect(() => {
    if (!xGetter || !yGetter) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    const handleResize = () => draw(context);

    const handleMouseMove = (event) => {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      mousePosition.current.x = event.clientX - rect.left;
      mousePosition.current.y = event.clientY - rect.top;
      draw(context);
    };

    canvas.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      canvas.removeEventListener("mousemove", handleMouseMove);
    };
  }, [draw, xGetter, yGetter]);

  if (!xGetter || !yGetter) {
    return null;
  }

  return (
    <Box px={3} display="flex" alignItems="center" flex={1} position="relative">
      <Box ref={containerRef} style={{ width: "100%" }}>
        <canvas ref={canvasRef} />
      </Box>
    </Box>
  );
};

export default RoomStatisticsChart;